import React from "react";
import ReactDOM from "react-dom/client";
import BarChartComponent from "./charts/BarChart";
import NegativeBarChartComponent from "./charts/NegativeBarChart";
import PieChartDuploComponent from "./charts/PieChartDuplo";
import ScatterChartComponent from "./charts/ScatterChart";
import StackedBarChartComponent from "./charts/StackedBarChart";
import CodeScannerComponent from "./components/CodeScanner";
import InventarioComponent from "./components/Inventario";
import LineChartComponent from "./charts/LineChart";
import PieChartSimplesComponent from "./charts/PieChartSimples";
import SincronizacaoProdutos from "./components/SincronizacaoProdutos";
import VincularFormasPagamento from "./components/VincularFormasPagamento";
import OrcamentoComponent from "./components/Orcamento";

// Função para inicializar React nos elementos específicos
function parseJSONAttr(el, attr, fallback = null) {
	try {
		const val = el.getAttribute(attr);
		return val ? JSON.parse(val) : fallback;
	} catch (e) {
		console.warn(`Erro ao fazer parse do atributo ${attr}:`, e);
		return fallback;
	}
}

/* 
    Antes de qualquer coisa, admito meu erro, acreditei que usaria o react apenas para gráficos -- estava errado --
por este motivo onde se observar a utilização de "Chart" leia-se "Component", initChart -> initComponent, chart-type -> component-type,
e assim por diante -- é claro que não em componentes de gráficos -- e desculpas por este meu descuido.
*/

function initChart(chart) {
	if (chart.getAttribute("data-react-init") === "true") return;

	const type = chart.getAttribute("chart-type");
	if (!type) return;

	const chartHandlers = {
		barChart: () => {
			const chartData = parseJSONAttr(chart, "data-chart", []);
			const chartLabel = parseJSONAttr(chart, "label-chart", []);
			const dataKeys = parseJSONAttr(chart, "data-keys", []);
			const hide = chart.getAttribute("chart-hide") || false;
			const dataType = chart.getAttribute("chart-dataType") || "currency";

			if (chartData.length && chartLabel.length >= 1 && dataKeys.length >= 1) {
				renderChart(
					chart,
					<BarChartComponent
						data={chartData}
						chartLabel={chartLabel}
						dataKeys={dataKeys}
						hide={hide}
						dataType={dataType}
					/>,
				);
			}
		},

		lineChart: () => {
			const chartData = parseJSONAttr(chart, "data-chart", []);
			const chartLabel = parseJSONAttr(chart, "label-chart", []);
			const dataKeys = parseJSONAttr(chart, "data-keys", []);

			if (chartData.length) {
				renderChart(
					chart,
					<LineChartComponent
						data={chartData}
						chartLabel={chartLabel}
						dataKeys={dataKeys}
					/>,
				);
			}
		},

		negativeBarChart: () => {
			const chartData = parseJSONAttr(chart, "data-chart", []);
			const chartLabel = parseJSONAttr(chart, "label-chart", []);
			const dataKeys = parseJSONAttr(chart, "data-keys", []);
			const dataLabels = parseJSONAttr(chart, "data-labels", []);

			if (chartData.length) {
				renderChart(
					chart,
					<NegativeBarChartComponent
						data={chartData}
						chartLabel={chartLabel}
						dataKeys={dataKeys}
						dataLabels={dataLabels}
					/>,
				);
			}
		},

		pieChartDuplo: () => {
			const chartData = {
				chartDataEntradas: parseJSONAttr(chart, "dataEntradas", []),
				chartDataSaidas: parseJSONAttr(chart, "dataSaidas", []),
			};
			const chartLabel = parseJSONAttr(chart, "label-chart", []);
			const dataKeys = parseJSONAttr(chart, "data-keys", []);

			renderChart(
				chart,
				<PieChartDuploComponent
					data={chartData}
					chartLabel={chartLabel}
					dataKeys={dataKeys}
				/>,
			);
		},

		pieChartSimples: () => {
			const chartData = parseJSONAttr(chart, "data-chart", []);
			const title = chart.getAttribute("title-chart");

			if (chartData.length) {
				renderChart(
					chart,
					<PieChartSimplesComponent data={chartData} title={title} />,
				);
			}
		},

		scatterChart: () => {
			const chartData = parseJSONAttr(chart, "data-chart", []);
			const dataKeys = parseJSONAttr(chart, "data-keys", []);
			const chartLabel = parseJSONAttr(chart, "label-chart", []);
			const title = chart.getAttribute("title-chart");

			renderChart(
				chart,
				<ScatterChartComponent
					data={chartData}
					dataKeys={dataKeys}
					chartLabel={chartLabel}
					title={title}
				/>,
			);
		},

		stackedBarChart: () => {
			const chartData = parseJSONAttr(chart, "data-chart", []);
			const dataKeys = parseJSONAttr(chart, "data-keys", []);
			const chartLabel = parseJSONAttr(chart, "label-chart", []);
			const title = chart.getAttribute("title-chart");
			const showDifferenceLabel = chart.getAttribute("showDifferenceLabel")
				? true
				: false;
			const dataType = chart.getAttribute("chart-dataType") || "currency";
			const pageByMonth = chart.hasAttribute("page-by-month");

			renderChart(
				chart,
				<StackedBarChartComponent
					data={chartData}
					dataKeys={dataKeys}
					chartLabel={chartLabel}
					title={title}
					showDifferenceLabel={showDifferenceLabel}
					dataType={dataType}
					pageByMonth={pageByMonth}
				/>,
			);
		},

		codeScanner: () => {
			const handleScanResult = (scannedData) => {
				const event = new CustomEvent("codeScanSuccess", {
					detail: { data: scannedData },
				});
				chart.dispatchEvent(event);
			};

			// Passa a função de callback para o componente
			renderChart(
				chart,
				<CodeScannerComponent onScanResult={handleScanResult} />,
			);
		},

		inventario: () => {
			// Pega o catálogo de produtos que o Handlebars colocou no HTML
			const catalogo = parseJSONAttr(chart, "data-catalogo", []);
			const inventarioItens = parseJSONAttr(chart, "data-inventario-itens", []);
			const inventarioId = chart.getAttribute("data-inventario-id");
			renderChart(
				chart,
				<InventarioComponent
					catalogoProdutos={catalogo}
					inventarioItens={inventarioItens}
					inventarioId={inventarioId}
				/>,
			);
		},
		
		sincronizacaoProdutos: () => {
			const tenant = chart.getAttribute("data-tenant");
			const provider = chart.getAttribute("data-provider");
			const providerLabel = chart.getAttribute("data-provider-label");
			renderChart(
				chart,
				<SincronizacaoProdutos
					tenant={tenant}
					provider={provider}
					providerLabel={providerLabel}
				/>,
			);
		},

		vincularFormasPagamento: () => {
			const tenant = chart.getAttribute("data-tenant");
			const provider = chart.getAttribute("data-provider");
			const providerLabel = chart.getAttribute("data-provider-label");
			renderChart(
				chart,
				<VincularFormasPagamento
					tenant={tenant}
					provider={provider}
					providerLabel={providerLabel}
				/>,
			);
		},

		orcamento: () => {
			const vendaId = chart.getAttribute("data-venda-id") || "";
			const vendedorPadrao = chart.getAttribute("data-vendedor-padrao") || "";
			const opcoes = parseJSONAttr(chart, "data-opcoes", { filiais: [], usuarios: [], almoxarifados: [] });
			const catalogo = parseJSONAttr(chart, "data-catalogo", []);
			const orcamento = parseJSONAttr(chart, "data-orcamento", null);
			renderChart(
				chart,
				<OrcamentoComponent
					vendaId={vendaId}
					vendedorPadrao={vendedorPadrao}
					opcoes={opcoes}
					catalogo={catalogo}
					orcamento={orcamento}
				/>,
			);
		},
	};

	try {
		const handler = chartHandlers[type];
		if (handler) handler();
	} catch (err) {
		console.error(`Erro ao renderizar componente do tipo ${type}:`, err);
	}
}

function renderChart(container, component) {
	const root = ReactDOM.createRoot(container);
	root.render(component);
	container.setAttribute("data-react-init", "true");
}

// Configura o MutationObserver para observar mudanças no DOM
const observer = new MutationObserver((mutationsList) => {
	for (const mutation of mutationsList) {
		if (mutation.type === "childList") {
			// Verifica se novos elementos com chart="true" foram adicionados ao DOM
			document.querySelectorAll('[chart="true"]').forEach((element) => {
				if (!element.getAttribute("data-react-init")) {
					// Verifica se o gráfico já foi inicializado
					initChart(element);
				}
			});
		}
	}
});

// Inicia a observação do DOM
observer.observe(document.body, { childList: true, subtree: true });

// Inicializa os gráficos imediatamente se os elementos já estiverem no DOM
document.querySelectorAll('[chart="true"]').forEach((element) => {
	initChart(element);
});